import { kanInloggen, type PersoonRij, type Rechtensoort, type Seniority } from '@vip/shared';

/**
 * Wat de personenlijst laat zien: zoeken, en drie filters.
 *
 * Pure functies met tests, zoals `begeleiding/begeleiding.ts` en
 * `onderwerpen/filter.ts`. De server levert één lijst; het zeven gebeurt hier.
 */

/** `alle` is geen filter maar de stand waarin er niet gefilterd wordt. Als eigen
 *  waarde en niet als `null`, omdat een keuzelijst een waarde moet hebben om
 *  iets te kunnen tonen. */
export const ALLE = 'alle';

/** Wie meerdere vinkjes heeft valt onder elk van die filters. "Toon de buddy's"
 *  gaat over wie er gesprekken aanlevert, en dat doet een Admin met
 *  buddyvinkje ook.
 *
 *  De soorten komen uit het model, zodat een vinkje dat erbij komt hier niet
 *  vergeten kan worden. `geen` staat los: dat gaat over wie de app niet in komt,
 *  en dat is meer dan "geen vinkje van deze drie", want wie View Only heeft logt wel in.
 *  Vandaar `kanInloggen`. */
export type RechtenFilter = typeof ALLE | Rechtensoort | 'geen';

/** `geen` is de stand voor wie nog geen niveau heeft. Die hoort erbij omdat het
 *  de vraag is die je stelt bij het bijwerken van de administratie: van wie
 *  staat het er nog niet in. */
export type NiveauFilter = typeof ALLE | Seniority | 'geen';

export interface Filters {
  zoek: string;
  rechten: RechtenFilter;
  niveau: NiveauFilter;
  /** Een opdracht-id, of `ALLE`. */
  opdracht: string;
}

export const GEEN_FILTERS: Filters = { zoek: '', rechten: ALLE, niveau: ALLE, opdracht: ALLE };

/** Staat er iets aan? Bepaalt of het scherm laat zien hoeveel er wegvalt. */
export function isGefilterd({ zoek, rechten, niveau, opdracht }: Filters): boolean {
  return zoek.trim() !== '' || rechten !== ALLE || niveau !== ALLE || opdracht !== ALLE;
}

function heeftRechten(p: PersoonRij, filter: RechtenFilter): boolean {
  if (filter === ALLE) return true;
  if (filter === 'geen') return !kanInloggen(p.rechten);
  return p.rechten[filter];
}

function heeftNiveau(p: PersoonRij, filter: NiveauFilter): boolean {
  if (filter === ALLE) return true;
  if (filter === 'geen') return !p.niveau;
  return p.niveau === filter;
}

/**
 * Zoeken op naam, adres en opdracht.
 *
 * Het adres is de uitzondering op de regel dat je alleen zoekt op wat je ook
 * ziet. Het stond onder de naam in de lijst en is daar weggehaald, want met een
 * adres per persoon werd elke regel twee regels hoog. Zoeken erop blijft, want
 * een adres plakken is de snelste manier om te vinden wie het is, en de rij die
 * je terugkrijgt verklaart zichzelf: de naam hoort bij dat adres.
 */
function past(p: PersoonRij, term: string): boolean {
  if (term === '') return true;
  return (
    p.naam.toLowerCase().includes(term) ||
    (p.email ?? '').toLowerCase().includes(term) ||
    p.opdrachten.some(
      (o) => o.naam.toLowerCase().includes(term) || o.opdrachtgever.toLowerCase().includes(term),
    )
  );
}

export function filterPersonen(personen: readonly PersoonRij[], filters: Filters): PersoonRij[] {
  const term = filters.zoek.trim().toLowerCase();
  return personen.filter(
    (p) =>
      past(p, term) &&
      heeftRechten(p, filters.rechten) &&
      heeftNiveau(p, filters.niveau) &&
      (filters.opdracht === ALLE || p.opdrachten.some((o) => o.id === filters.opdracht)),
  );
}
